import { useEffect, useState } from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ManorMark } from '@/components/ui/manor-mark';
import type { Device } from '@/types/api';
import { isTargetConnected, type ConnectingTarget } from '../utils/connecting-target';
import connectionVideo from '@/assets/branding/connection-animation.mp4';
import connectionPoster from '@/assets/branding/connection-animation-poster.png';

interface SetupConnectingPanelProps {
  /** The device just set up. Parent should mount this panel keyed by deviceId. */
  target: ConnectingTarget;
  /** Live polled devices — the panel reads recognition from here. */
  devices: Device[];
  onDismiss: () => void;
  /** How long to wait before offering a "still waiting" escape hatch. */
  timeoutMs?: number;
}

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Prominent "Connecting…" state shown after a meter is set up, replacing the
// glowing-circle idea with the manor connection animation. It resolves to
// "Connected!" the moment the polled device flips to `recognized` (magnet
// transmission / join landed). Resolution keys off device STATUS, never off a
// successful fetch, so a transient poll error can't false-resolve it.
export function SetupConnectingPanel({
  target,
  devices,
  onDismiss,
  timeoutMs = 90_000,
}: SetupConnectingPanelProps) {
  const [phase, setPhase] = useState<'connecting' | 'connected' | 'timeout'>('connecting');
  const [videoFailed, setVideoFailed] = useState(false);
  const staticFallback = prefersReducedMotion || videoFailed;

  // Resolve when the target device reads `recognized` in the polled list.
  useEffect(() => {
    if (phase !== 'connecting') return;
    if (isTargetConnected(devices, target)) setPhase('connected');
  }, [devices, target, phase]);

  // Auto-dismiss shortly after success so the grid takes over.
  useEffect(() => {
    if (phase !== 'connected') return;
    const t = window.setTimeout(onDismiss, 2200);
    return () => window.clearTimeout(t);
  }, [phase, onDismiss]);

  // Escape hatch: never trap the installer in an endless "Connecting".
  useEffect(() => {
    if (phase !== 'connecting') return;
    const t = window.setTimeout(() => setPhase('timeout'), timeoutMs);
    return () => window.clearTimeout(t);
  }, [phase, timeoutMs]);

  const meta = (
    <p className="text-sm text-gray-500 dark:text-gray-400">
      <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{target.serialNumber}</span>
      {' · '}Unit <span className="font-medium text-gray-700 dark:text-gray-300">{target.unitNumber}</span>
    </p>
  );

  return (
    <Card variant="elevated" className="relative overflow-hidden p-0" aria-live="polite">
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute right-3 top-3 z-10 rounded p-1 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col items-center gap-4 bg-gray-900 px-6 py-8 text-center">
        {phase === 'connected' ? (
          <>
            <ManorMark tone="green" className="h-20 w-auto" />
            <div>
              <h3 className="font-display text-2xl font-bold text-white">Connected!</h3>
              <p className="mt-1 text-sm text-gray-300">
                <span className="font-mono">{target.serialNumber}</span> joined at Unit {target.unitNumber}.
              </p>
            </div>
            <CheckCircle2 className="h-6 w-6 text-success-500" aria-hidden="true" />
          </>
        ) : staticFallback ? (
          <>
            <ManorMark tone="blue" className="h-20 w-auto animate-pulse motion-reduce:animate-none" />
            <div>
              <h3 className="font-display text-2xl font-bold text-white">
                {phase === 'timeout' ? 'Still waiting…' : 'Connecting…'}
              </h3>
              <p className="mt-1 text-sm text-gray-300">
                <span className="font-mono">{target.serialNumber}</span> · Unit {target.unitNumber}
              </p>
            </div>
          </>
        ) : (
          <>
            <video
              src={connectionVideo}
              poster={connectionPoster}
              autoPlay
              loop
              muted
              playsInline
              onError={() => setVideoFailed(true)}
              className="h-44 w-auto rounded-lg"
            />
            <div>
              <h3 className="font-display text-2xl font-bold text-white">
                {phase === 'timeout' ? 'Still waiting…' : 'Connecting…'}
              </h3>
              <p className="mt-1 text-sm text-gray-300">
                {phase === 'timeout'
                  ? 'No transmission yet — check the meter, or keep waiting.'
                  : 'Waiting for the magnet transmission to land.'}
              </p>
            </div>
          </>
        )}

        {phase === 'timeout' && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPhase('connecting')}>
              Keep waiting
            </Button>
            <Button variant="ghost" size="sm" onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white px-6 py-3 dark:bg-gray-800">{meta}</div>
    </Card>
  );
}
